import type { ReactNode } from "react";

export type FilterType = "checkbox" | "number-range" | "text-input" | "date-range" | "custom";

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterConfig {
  key: string;
  header: string;
  type: FilterType;
  options?: FilterOption[];
  multiSelect?: boolean;
  unitPrefix?: string;
  unitSuffix?: string;
  placeholder?: string;
  formatChipLabel?: (value: string) => string;
  /** "custom" only: draws the section's body from its pending values. */
  render?: (selected: string[], setSelected: (values: string[]) => void) => ReactNode;
}

export interface DateRangeValue {
  from: string;
  to: string;
}

export interface NumberRangeValue {
  op: "gt" | "lt" | "between";
  v1: string;
  v2: string;
}

export const dateRangeFilter = {
  encode: ({ from, to }: DateRangeValue): string => `${from}:${to}`,
  decode: (value: string): DateRangeValue => {
    const [from = "", to = ""] = value.split(":");
    return { from, to };
  },
};

export const numberRangeFilter = {
  encode: ({ op, v1, v2 }: NumberRangeValue): string =>
    op === "between" ? `${op}:${v1}:${v2}` : `${op}:${v1}`,
  decode: (value: string): NumberRangeValue => {
    const [op = "gt", v1 = "", v2 = ""] = value.split(":");
    return { op: op as NumberRangeValue["op"], v1, v2 };
  },
};

export const callHistoryFilterConfig: FilterConfig[] = [
  {
    key: "timeRange",
    header: "Time Range",
    type: "date-range",
    formatChipLabel: (value) => {
      const { from, to } = dateRangeFilter.decode(value);
      if (from && to) return `${from} – ${to}`;
      if (from) return `From ${from}`;
      if (to) return `Until ${to}`;
      return value;
    },
  },
  {
    key: "callType",
    header: "Call Type",
    type: "checkbox",
    multiSelect: true,
    options: [
      { label: "Inbound", value: "inbound" },
      { label: "Outbound", value: "outbound" },
      { label: "Web", value: "web" },
    ],
  },
  {
    // Drawn by CallHistory: a searchable, paged list, because a workspace's
    // agents are too many to download just to fill a filter.
    key: "agent",
    header: "Agent",
    type: "custom",
  },
  {
    key: "campaign",
    header: "Campaign",
    type: "checkbox",
    multiSelect: true,
  },
  {
    key: "from",
    header: "From Number",
    type: "checkbox",
    multiSelect: true,
  },
  {
    key: "to",
    header: "To Number",
    type: "text-input",
    placeholder: "e.g. 5551234 or +1234567890",
  },
  {
    key: "duration",
    header: "Duration",
    type: "number-range",
    unitSuffix: "mins",
    formatChipLabel: (value) => {
      const { op, v1, v2 } = numberRangeFilter.decode(value);
      if (op === "gt") return `> ${v1} mins`;
      if (op === "lt") return `< ${v1} mins`;
      if (op === "between") return `${v1} – ${v2} mins`;
      return value;
    },
  },
  {
    key: "cost",
    header: "Cost",
    type: "number-range",
    unitPrefix: "$",
    formatChipLabel: (value) => {
      const { op, v1, v2 } = numberRangeFilter.decode(value);
      if (op === "gt") return `> $${v1}`;
      if (op === "lt") return `< $${v1}`;
      if (op === "between") return `$${v1} – $${v2}`;
      return value;
    },
  },
  {
    key: "callId",
    header: "Call ID",
    type: "text-input",
    placeholder: "Paste full Call ID (UUID)...",
  },
  {
    key: "disconnectionReason",
    header: "Disconnection Reason",
    type: "checkbox",
    multiSelect: true,
    options: [
      { label: "User Hangup", value: "user_hangup" },
      { label: "Agent Hangup", value: "agent_hangup" },
      { label: "Call Transfer", value: "call_transfer" },
      { label: "Voicemail Reached", value: "voicemail_reached" },
      { label: "Inactivity", value: "inactivity" },
      { label: "Max Duration", value: "max_duration_reached" },
      { label: "Dial Busy", value: "dial_busy" },
      { label: "Dial Failed", value: "dial_failed" },
      { label: "No Answer", value: "dial_no_answer" },
      // Calls the assistant ended itself after failing to recover from a
      // technical fault. Previously these were indistinguishable from ordinary
      // caller hangups, which is why a month of them went unnoticed.
      { label: "System Error", value: "system_error" },
    ],
  },
];
